import styled from 'styled-components';
import { useEffect, useState } from 'react';

import {
  IconChevronDown,
  IconMessage,
  IconDefaultProfile,
  IconExport,
  IconAbsent,
  IconCheckInCircle,
  IconWarning,
} from 'assets/icons';
import ClipboardCopy from 'components/ClipboardCopy/ClipboardCopy';

import {
  Booking,
  BookingState,
  OrderTransactionActionStatus,
  TicketState,
} from 'types/api';
import { formatPhoneNumber } from 'utils/formatting';
import CheckBox from 'components/common/CheckBox';
import dayjs from 'dayjs';
import ImgComponent from 'components/ImgComponent/ImgComponent';

const UserCardContainer = styled.div<{ $isChecked: boolean }>`
  display: flex;
  flex-direction: column;

  width: 100%;

  border-radius: 12px;
  border: ${({ $isChecked }) =>
    $isChecked ? '1.5px solid #414B56' : '1.5px solid #F7F7F8'};

  background-color: #f7f7f8;
`;

const UserBriefInfoBox = styled.div<{ $hasWarningText: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;

  width: 100%;

  ${({ $hasWarningText }) =>
    `
      padding: 20px 16px ${$hasWarningText ? '0px' : '20px'};
    `}

  cursor: pointer;

  > img[data-type='thumbnail'] {
    min-width: 52px;
    min-height: 52px;
    max-width: 52px;
    max-height: 52px;

    border-radius: 16px;
  }
`;

const ChevronBox = styled.div<{ $isOpen: boolean; $marginLeft?: string }>`
  display: flex;
  align-items: center;
  gap: 4px;

  cursor: pointer;
  pointer-events: none;

  transition: transform 0.3s ease-in-out;

  ${({ $isOpen, $marginLeft }) =>
    `
      transform: ${$isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
      margin-left: ${$marginLeft};
    `}
`;

const UserInfoBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  > .basic-info-box {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;

    > p {
      margin: 0px;

      font-weight: 500;
      font-size: 16px;
      line-height: 22px;

      color: rgba(70, 71, 76, 1);

      &[data-type='male'] {
        font-weight: 400;
        font-size: 14px;
        line-height: 20px;

        color: rgba(48, 54, 209, 1);
      }

      &[data-type='female'] {
        font-weight: 400;
        font-size: 14px;
        line-height: 20px;

        color: rgba(162, 109, 231, 1);
      }

      &[data-type='name'] {
        font-weight: 500;
        font-size: 16px;
        line-height: 22px;
      }

      &[data-type='phone'] {
        font-weight: 500;
        font-size: 16px;
        line-height: 22px;
      }

      @media (max-width: 768px) {
        font-size: 14px;
        line-height: 20px;
      }
    }
  }

  > p[data-type='opt-name'] {
    font-weight: 400;
    font-size: 15px;
    line-height: 22px;

    color: rgba(152, 155, 162, 1);
  }
`;

const CancelRequestBox = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;

  padding: 0px 16px 16px;
  margin-top: 16px;
`;

const WarningText = styled.p`
  margin: 0 !important;

  font-weight: 500 !important;
  font-size: 13px !important;
  line-height: 18px !important;
  color: #ff4242 !important;
`;

const UserDetailInfoBox = styled.div`
  display: flex;
  flex-direction: column;

  width: 100%;

  padding: 0px 16px;

  p[data-type='total-count'] {
    margin: 4px 0 16px;

    font-weight: 500;
    font-size: 16px;
    line-height: 22px;

    color: rgba(70, 71, 76, 1);
  }
`;

const UserCardDetailBox = styled.div<{ $isLast: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;

  margin-bottom: ${({ $isLast }) => ($isLast ? '0px' : '24px')};
  padding-bottom: ${({ $isLast }) => ($isLast ? '0px' : '24px')};

  border-bottom: ${({ $isLast }) => ($isLast ? '0px' : '1px solid')};
  border-color: rgba(234, 235, 236, 1);
`;

const DataRow = styled.div<{ $gap?: string }>`
  display: flex;
  align-items: flex-start;
  gap: ${({ $gap }) => ($gap ? $gap : '12px')};

  p[data-type='ticket-title'] {
    font-weight: 500;
    font-size: 16px;
    line-height: 22px;

    color: rgba(65, 75, 86, 1);

    &.cancel {
      text-decoration: line-through;
    }
  }

  p[data-type='booking-text'] {
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;

    color: rgba(152, 155, 162, 1);
  }

  p[data-type='bookingId-text'] {
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;

    color: rgba(152, 155, 162, 1);
  }

  p[data-type='query-text'] {
    min-width: 25px;

    font-weight: 500;
    font-size: 14px;
    line-height: 20px;

    color: rgba(70, 71, 76, 1);
  }

  p[data-type='answer-text'] {
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;

    color: rgba(112, 115, 124, 1);
  }
`;

const WhiteBookingItemBoxContainer = styled.div`
  display: flex;
  flex-direction: column;

  border: 1px solid;
  border-color: rgba(234, 235, 236, 1);
  border-radius: 16px;

  padding: 20px 16px;

  background-color: #fff;

  > div[data-type='single-query'] {
    display: flex;
    flex-direction: column;
    gap: 8px;

    margin-bottom: 16px;
    padding-bottom: 16px;

    border-bottom: 1px solid;
    border-color: rgba(234, 235, 236, 1);

    &.last {
      padding-bottom: 0px;
      margin-bottom: 0px;
      border-bottom: 0;
    }
  }
`;

const WhiteQueryBoxContainer = styled.div`
  display: flex;
  flex-direction: column;

  border: 1px solid;
  border-color: rgba(234, 235, 236, 1);
  border-radius: 16px;

  padding: 16px;

  background-color: #fff;

  > div[data-type='single-query'] {
    display: flex;
    flex-direction: column;
    gap: 8px;

    margin-bottom: 16px;
    padding-bottom: 16px;

    border-bottom: 1px solid;
    border-color: rgba(234, 235, 236, 1);

    &.last {
      padding-bottom: 0px;
      margin-bottom: 0px;
      border-bottom: 0;
    }
  }
`;

const Badge = styled.div<{
  $status: BookingState | OrderTransactionActionStatus | TicketState;
}>`
  height: 24px;
  min-width: 51px;
  width: fit-content;

  padding: 0px 6px;

  border-radius: 6px;

  font-weight: 600;
  font-size: 11px;
  line-height: 24px;

  ${({ $status }) => `
    background-color: ${
      $status === BookingState.ABSENT
        ? 'rgba(255, 230, 230, 1)'
        : $status === OrderTransactionActionStatus.CANCELLATION_REQUESTED
        ? 'rgba(255, 230, 230, 1)'
        : $status === OrderTransactionActionStatus.CANCELLATION_COMPLETED
        ? 'rgba(255, 230, 230, 1)'
        : $status === BookingState.APPLIED
        ? 'rgba(234, 235, 236, 1)'
        : $status === BookingState.CANCELED
        ? 'rgba(255, 230, 230, 1)'
        : $status === BookingState.CONFIRMED
        ? 'rgba(213, 233, 255, 1)'
        : $status === BookingState.USED
        ? 'rgba(213, 233, 255, 1)'
        : 'rgba(255, 235, 238, 1)'
    };

    color: ${
      $status === BookingState.ABSENT
        ? 'rgba(255, 66, 66, 1)'
        : $status === OrderTransactionActionStatus.CANCELLATION_REQUESTED
        ? 'rgba(255, 66, 66, 1)'
        : $status === OrderTransactionActionStatus.CANCELLATION_COMPLETED
        ? 'rgba(255, 66, 66, 1)'
        : $status === BookingState.APPLIED
        ? 'rgba(70, 71, 76, 1)'
        : $status === BookingState.CANCELED
        ? 'rgba(255, 66, 66, 1)'
        : $status === BookingState.CONFIRMED
        ? 'rgba(0, 113, 233, 1)'
        : $status === BookingState.USED
        ? 'rgba(0, 113, 233, 1)'
        : 'rgba(255, 235, 238, 1)'
    };
  `}
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;

  margin-top: 24px;
  padding-bottom: 20px;

  > button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;

    height: 40px;
    padding: 0 12px;

    border-radius: 12px;

    font-weight: 600;
    font-size: 14px;
    line-height: 40px;
    color: rgba(65, 75, 86, 1);

    background-color: rgba(234, 235, 236, 1);

    &[data-type='cancel'] {
      background-color: #ffe6e6;
      color: #ff4242;
    }

    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const CheckAttendanceBox = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  margin-left: auto;

  > p {
    margin: 0;
    color: #989ba2;
    font-weight: 500;
    font-size: 16px;
    line-height: 22px;
    margin-bottom: 0;

    white-space: nowrap;
  }

  > p[data-type='ABSENT'] {
    color: #ff4242;
  }

  > p[data-type='USED'] {
    color: #0071e9;
  }
`;

const TimelineBox = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 16px;

  border-top: 1px solid;
  border-color: #eaebec;

  padding-top: 24px;
  margin-top: 24px;

  > div {
    display: flex;
    gap: 16px;

    > p[data-type='timeline-title'] {
      font-weight: 500;
      font-size: 15px;
      line-height: 22px;

      color: #414b56;
    }

    > p[data-type='timeline-date'] {
      font-weight: 400;
      font-size: 15px;
      line-height: 22px;

      color: #70737c;
    }
  }
`;

interface Props {
  data: Booking;
  isSelectedUser: boolean;
  handleShowUserCardDetail: (itemId: string) => void;
  idx: number;
  selectedItemId: string | null;
  goToSalesPage: (orderId: string) => void;
  goToMessagePage: (orderId: string) => void;
  needToCheckAttendance?: boolean;
  checkUserHandler?: (ticketIdList: string[], bookingId: string) => void;
  checkedList?: Record<string, string[]>;
  onClickCancelAbsent?: (checkedTicketIdList: string[]) => void;
  onClickCancelAttendance?: (checkedTicketIdList: string[]) => void;
  timeline?: {
    confirmedAt: string;
    usedAt: string;
    absentAt: string;
  };
  handleRestoreTickets?: (ticketIds: string[], cb?: () => void) => void;
  handleRefetchBookingItemList?: () => void;
  ticketType?: 'fixed' | 'unfixed';
  startTime?: number;
  handleCancelBookingConfirmModal: () => void;
  saveSelectedConfirmedBookingIds: (
    selectedConfirmedBookingIds: string[],
  ) => void;
  ticketOriginType?: 'fixed' | 'unfixed';
}

function MultipleBookingUserCardV2({
  data,
  isSelectedUser,
  handleShowUserCardDetail,
  idx,
  selectedItemId,
  goToSalesPage,
  goToMessagePage,
  needToCheckAttendance = false,
  checkUserHandler,
  checkedList,
  onClickCancelAbsent,
  onClickCancelAttendance,
  timeline = null,
  handleRefetchBookingItemList,
  handleRestoreTickets,
  ticketType,
  startTime,
  handleCancelBookingConfirmModal,
  saveSelectedConfirmedBookingIds,
  ticketOriginType,
}: Props) {
  const realTicketListByItem = data.items.map(item => {
    return item.tickets.filter(ticket => {
      return ticket?.scheduleTerm?.startedAt === startTime;
    });
  });

  const realTicketList = data.items
    .map(item => {
      return item.tickets.filter(ticket => {
        return ticket?.scheduleTerm?.startedAt === startTime;
      });
    })
    .flat();

  const findCanceledTicketIndexNoList = () => {
    const bookingId = data.id;
    const orderData = data.order;

    const filteredOrderItem = orderData.items.filter(item => {
      if (item.id === bookingId) {
        return item;
      }
    });

    const ticketIndexNo = filteredOrderItem
      .map(item => {
        return item?.dependedItems.map(item => {
          return item?.units.map(item => {
            return item?.indexNo;
          });
        });
      })
      .flat();

    return ticketIndexNo.flat();
  };

  const canceledTicketIndexNoList = findCanceledTicketIndexNoList();

  const availableTicketList = realTicketListByItem
    ?.filter((dataArray, itemIdx) => {
      /*  취소요청이 있는 티켓은 제외 (indexNo 기준) */
      const itemIndexNo = data.items[itemIdx]?.indexNo;
      if (canceledTicketIndexNoList?.includes(itemIndexNo)) {
        return;
      }

      return dataArray;
    })
    .flat()
    .filter(data => data.status !== TicketState.CANCELED);

  const ticketIdList = availableTicketList.map(data => data.id);

  const isChecked = checkedList?.[data.id]?.includes(
    availableTicketList[0]?.id,
  );

  const attendanceResult = availableTicketList[0]?.status;

  const getBadgeText = (
    bookingState: BookingState | OrderTransactionActionStatus | TicketState,
  ) => {
    switch (bookingState) {
      case OrderTransactionActionStatus.CANCELLATION_REQUESTED:
        return '취소요청';
      case OrderTransactionActionStatus.CANCELLATION_COMPLETED:
        return '취소완료';
      case BookingState.ABSENT:
        return '예약불참';
      case BookingState.APPLIED:
        return '예약대기';
      case BookingState.CANCELED:
        return '취소완료';
      case BookingState.CONFIRMED:
        return '예약확정';
      case BookingState.USED:
        return '예약참석';
      default:
        return '';
    }
  };

  const transactionItemList = data.order.transactions
    .map(data => {
      if (data.items.length > 0) {
        return data.items
          .map(item => ({
            [item.id]: data.actionStatus,
          }))
          .flat();
      }
    })
    .flat();

  const orderTrxData = transactionItemList.reduce((acc, item) => {
    const key = Object.keys(item)[0];
    const value = item[key];
    return { ...acc, [key]: value };
  }, {});

  const cancelRequestItemsIdxOrder = data.order.items
    .map(data => {
      return data.dependedItems.map(data => {
        return {
          id: data.id,
          units: data.units.map(unit => unit.indexNo),
        };
      });
    })
    .flat()
    .map((data, idx) => {
      const status = orderTrxData[data.id];

      return {
        ...data,
        status: status,
      };
    });

  const bookingOrderData = data.order.items.find(item => item.id === data.id);

  const sameDateItemData = data.items.filter(item => {
    if (
      item.tickets.some(ticket => ticket?.scheduleTerm?.startedAt === startTime)
    ) {
      return item;
    }
  });

  const filteredItems = {
    ...data,
    items: [...sameDateItemData],
  };

  const dependedItemList = bookingOrderData.dependedItems
    .map(item => {
      const units = item.units.map(it => it.indexNo);
      let id = [];

      units.forEach(unit => {
        if (filteredItems.items.map(it => it.indexNo).includes(unit)) {
          id.push(item.id);
        }
      });

      return id;
    })
    .flat();

  const canceledRequest = dependedItemList.some(
    itemId =>
      orderTrxData[itemId] ===
      OrderTransactionActionStatus.CANCELLATION_REQUESTED,
  );

  const canceledCompleted = dependedItemList.some(
    itemId =>
      orderTrxData[itemId] ===
      OrderTransactionActionStatus.CANCELLATION_COMPLETED,
  );

  let multipleTicketListCount = 0;

  return (
    <UserCardContainer $isChecked={isChecked} key={`booking-user-list-${idx}`}>
      <UserBriefInfoBox
        $hasWarningText={canceledRequest || canceledCompleted}
        onClick={() => {
          handleShowUserCardDetail(data.id);
        }}
      >
        <ImgComponent
          src={data.customer?.profileImage?.uri}
          alt="thumbnail"
          style={{
            minWidth: '52px',
            minHeight: '52px',
            maxWidth: '52px',
            maxHeight: '52px',

            borderRadius: '16px',
          }}
        />

        <UserInfoBox>
          <div className="basic-info-box">
            {data.customer?.gender && (
              <>
                <p
                  data-type={data.customer?.gender === 'F' ? 'female' : 'male'}
                >
                  {data.customer?.gender === 'F' ? '여자' : '남자'}
                </p>

                {`\u2022`}
              </>
            )}

            <p data-type="name">{data.customer?.nickname}</p>

            {data.customer?.mobileNumber && (
              <>
                {`\u2022`}
                <p data-type="phone">
                  {formatPhoneNumber(data.customer?.mobileNumber)}
                </p>
              </>
            )}

            <ClipboardCopy value={data.customer?.mobileNumber} />

            {needToCheckAttendance && (
              <ChevronBox
                $isOpen={data.id === selectedItemId}
                $marginLeft={'0px'}
              >
                <IconChevronDown
                  width={24}
                  height={24}
                  color={'rgba(112, 115, 124, 1)'}
                />
              </ChevronBox>
            )}
          </div>

          {!isSelectedUser && (
            <p data-type="opt-name">
              {data.info?.name}
              {realTicketList.flat().length > 1
                ? ` 외 ${realTicketList.flat().length - 1}개 (총 ${
                    realTicketList.flat().length
                  }건)`
                : ''}
            </p>
          )}
        </UserInfoBox>
        {needToCheckAttendance ? (
          <CheckAttendanceBox>
            {[TicketState.ABSENT, TicketState.USED].includes(
              attendanceResult,
            ) ? (
              <>
                {attendanceResult === TicketState.ABSENT ? (
                  <IconAbsent width={20} height={20} />
                ) : (
                  <IconCheckInCircle width={20} height={20} color={'#0071E9'} />
                )}
                <p data-type={attendanceResult}>
                  {attendanceResult === TicketState.ABSENT ? '불참' : '참석'}
                </p>
              </>
            ) : ticketIdList.length === 0 ? null : (
              <>
                <CheckBox
                  isChecked={isChecked}
                  onChange={() => {
                    checkUserHandler(ticketIdList, data.id);
                  }}
                  style={{
                    width: '16px',
                    height: '16px',
                    borderRadius: '2px',
                    borderColor: isChecked ? '#000' : '#989BA2',
                  }}
                  label="선택"
                />
              </>
            )}
          </CheckAttendanceBox>
        ) : (
          <ChevronBox $isOpen={data.id === selectedItemId} $marginLeft={'auto'}>
            <IconChevronDown
              width={24}
              height={24}
              color={'rgba(112, 115, 124, 1)'}
              // onClick={() => handleShowUserCardDetail(data.id)}
            />
          </ChevronBox>
        )}
      </UserBriefInfoBox>

      {(canceledRequest || canceledCompleted) && (
        <CancelRequestBox>
          <IconWarning />
          <WarningText>
            {canceledRequest
              ? '주문 취소가 요청된 예약건이 있어요.'
              : canceledCompleted
              ? '취소된 예약건이 있어요.'
              : ''}
          </WarningText>
        </CancelRequestBox>
      )}

      {selectedItemId === data.id && (
        <UserDetailInfoBox>
          {realTicketList.flat().length > 1 && (
            <p data-type="total-count">총 {realTicketList.flat().length}건</p>
          )}

          {data.items.map(({ replies, tickets, booking, indexNo }, itemIdx) => {
            const { id: bookingId } = booking;

            // console.log('data', data);

            const bookingOrderData = data.order.items.find(
              data => data.id === bookingId,
            );

            const dependedItemList = bookingOrderData.dependedItems.find(
              dependedItem => {
                return (
                  dependedItem.units.filter(unit => {
                    return unit.indexNo === itemIdx;
                  }).length > 0
                );
              },
            );

            const canceledItem = dependedItemList?.units?.some(
              x => x.indexNo === indexNo,
            )
              ? dependedItemList
              : null;

            const multipleTicketList = tickets.filter(
              ticket => ticket?.scheduleTerm?.startedAt === startTime,
            );

            return multipleTicketList.map((ticket, ticketIdx) => {
              multipleTicketListCount = multipleTicketListCount + 1;

              const ticketStatus = ticket.status;

              const bookingStatus = canceledItem
                ? orderTrxData[canceledItem.id]
                : ticketStatus;

              return (
                <UserCardDetailBox
                  $isLast={realTicketList.length === multipleTicketListCount}
                  key={`booking-user-detail-${ticketIdx}`}
                >
                  <WhiteBookingItemBoxContainer>
                    <DataRow>
                      <Badge $status={bookingStatus}>
                        {getBadgeText(bookingStatus)}
                      </Badge>
                      <p
                        data-type="ticket-title"
                        className={canceledItem ? 'cancel' : ''}
                      >
                        {ticket.bookingItem.booking.info.options[0].name}
                      </p>
                    </DataRow>
                  </WhiteBookingItemBoxContainer>

                  {replies.length > 0 && (
                    <WhiteQueryBoxContainer>
                      {replies.map(({ title, answer, selections }, idx) => {
                        const getAnswerText = () => {
                          if (answer) {
                            return answer;
                          }

                          if (selections) {
                            return selections
                              .map(data => data.label)
                              .join(', ');
                          }

                          return '-';
                        };

                        return (
                          <div
                            key={`booking-user-detail-query-${idx}`}
                            data-type="single-query"
                            className={replies.length - 1 === idx ? 'last' : ''}
                          >
                            <DataRow>
                              <p data-type="query-text">질문</p>
                              <p data-type="answer-text">{title}</p>
                            </DataRow>
                            <DataRow>
                              <p data-type="query-text">답변</p>
                              <p data-type="answer-text">
                                {getAnswerText() || '-'}
                              </p>
                            </DataRow>
                          </div>
                        );
                      })}
                    </WhiteQueryBoxContainer>
                  )}
                </UserCardDetailBox>
              );
            });
          })}

          {realTicketList[0]?.confirmedAt && (
            <TimelineBox>
              <div>
                <p data-type="timeline-title">예약 확정일</p>
                <p data-type="timeline-date">
                  {dayjs(realTicketList[0]?.confirmedAt).format(
                    'YYYY년 M월 D일 HH:mm',
                  )}
                </p>
              </div>
              <div>
                <p data-type="timeline-title">참석/불참일</p>
                <p data-type="timeline-date">
                  {!realTicketList[0]?.usedAt && !realTicketList[0]?.absentAt
                    ? '-'
                    : dayjs(
                        realTicketList[0]?.usedAt
                          ? realTicketList[0]?.usedAt
                          : realTicketList[0]?.absentAt
                          ? realTicketList[0]?.absentAt
                          : '-',
                      ).format('YYYY년 M월 D일 HH:mm')}
                </p>
              </div>
            </TimelineBox>
          )}

          <ButtonContainer>
            <button onClick={() => goToSalesPage(data.order.id)}>
              <IconExport />
              판매내역
            </button>
            <button onClick={() => goToMessagePage(data.order.id)}>
              <IconMessage />
              메시지
            </button>
            {ticketOriginType === 'fixed' ? (
              <></>
            ) : (
              /* 조율형일때만 노출 */
              attendanceResult === TicketState.CONFIRMED &&
              ticketType === 'unfixed' && (
                <button
                  data-type="cancel"
                  onClick={() => {
                    saveSelectedConfirmedBookingIds(
                      realTicketList.map(data => data.id),
                      // data.tickets.map(data => data.id),
                    );
                    handleCancelBookingConfirmModal();
                  }}
                >
                  예약 확정 취소
                </button>
              )
            )}

            {TicketState.ABSENT === attendanceResult && (
              <button
                data-type="cancel"
                onClick={() => onClickCancelAbsent(ticketIdList)}
              >
                불참 취소
              </button>
            )}
            {TicketState.USED === attendanceResult && (
              <button
                data-type="cancel"
                onClick={() => onClickCancelAttendance(ticketIdList)}
              >
                참석 취소
              </button>
            )}
          </ButtonContainer>
        </UserDetailInfoBox>
      )}
    </UserCardContainer>
  );
}

export default MultipleBookingUserCardV2;
